import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, CheckCircle2, Check, Play, Gamepad2, Lock, Crown } from "lucide-react";
import { api } from "@/lib/api";
import Icon from "@/components/Icon";
import { catColor } from "@/lib/colors";

const TYPE_LABEL = { quick_read: "Quick Read", quiz: "Quiz", flashcards: "Flashcards", checklist: "Checklist", decision_tree: "Decision Tree" };

export default function CategoryDetail() {
  const { key } = useParams();
  const navigate = useNavigate();
  const [cat, setCat] = useState(null);

  useEffect(() => { api.get(`/categories/${key}`).then((r) => setCat(r.data)).catch(() => {}); }, [key]);

  if (!cat) return <div className="grid place-items-center py-32"><Loader2 className="animate-spin text-indigo-400" size={32} /></div>;
  const col = catColor(cat.color);

  return (
    <div>
      <button data-testid="back-btn" onClick={() => navigate("/app/worlds")} className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground text-sm font-semibold mb-5 transition-colors"><ArrowLeft size={16} /> Worlds</button>

      <div className={`rounded-3xl p-7 glass border ${col.border} relative overflow-hidden mb-7`}>
        <div className={`absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gradient-to-br ${col.grad} opacity-20 blur-3xl`} />
        <div className={`h-16 w-16 rounded-2xl bg-gradient-to-br ${col.grad} grid place-items-center mb-4 shadow-xl`}>
          <Icon name={cat.icon} size={32} className="text-white" />
        </div>
        <h1 className="font-display font-extrabold text-3xl tracking-tight mb-2">{cat.name}</h1>
        <p className="text-muted-foreground max-w-2xl">{cat.description}</p>
        <div className="flex flex-wrap gap-2 mt-4">
          {cat.topics?.slice(0, 8).map((t) => <span key={t} className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-muted-foreground">{t}</span>)}
        </div>
      </div>

      {cat.simulations?.length > 0 && (
        <div className="mb-8">
          <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><Gamepad2 size={18} className={col.text} /> Simulations</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {cat.simulations.map((s) => (
              <motion.button key={s.key} whileHover={{ y: -4 }} data-testid={`cat-sim-${s.key}`} onClick={() => s.locked ? navigate("/app/premium") : navigate(`/simulation/${s.key}`)} className={`text-left rounded-2xl p-5 glass border ${s.locked ? "border-yellow-500/25" : col.border}`}>
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-display font-bold text-lg">{s.title}</h3>
                  {s.locked && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-300 border border-yellow-500/25"><Crown size={11} /> Premium</span>}
                </div>
                <p className="text-muted-foreground text-sm mb-3 line-clamp-2">{s.description}</p>
                {s.locked ? <span className="inline-flex items-center gap-1.5 text-sm font-bold text-yellow-400"><Lock size={14} /> Unlock with Premium</span>
                  : <span className={`inline-flex items-center gap-1.5 text-sm font-bold ${col.text}`}><Play size={14} fill="currentColor" /> Play · +{s.xp} XP</span>}
              </motion.button>
            ))}
          </div>
        </div>
      )}

      {(() => {
        const ls = cat.lessons;
        const n = ls.length;
        const t = Math.ceil(n / 3);
        const parts = [
          { tag: "Chapter 1", name: "Foundations", icon: "book", lessons: ls.slice(0, t) },
          { tag: "Chapter 2", name: "Level Up", icon: "trending-up", lessons: ls.slice(t, t * 2) },
          { tag: "Chapter 3", name: "Mastery", icon: "trophy", lessons: ls.slice(t * 2) },
        ];
        const currentId = (cat.lessons.find((l) => !l.completed && !l.locked) || {}).id;
        const chapters = parts.filter((c) => c.lessons.length);
        return (
          <div>
            <h2 className="font-display font-bold text-lg mb-1">Your Journey</h2>
            <p className="text-sm text-muted-foreground mb-6">Progress from foundations to mastery, one chapter at a time.</p>
            <div className="space-y-8">
              {chapters.map((ch, ci) => {
                const done = ch.lessons.filter((l) => l.completed).length;
                const pct = Math.round((done / ch.lessons.length) * 100);
                return (
                  <motion.div key={ch.tag} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: ci * 0.08 }}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`relative h-12 w-12 rounded-2xl bg-gradient-to-br ${col.grad} grid place-items-center shadow-lg shrink-0`}>
                        <Icon name={ch.icon} size={22} className="text-white" />
                        {pct === 100 && <span className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-emerald-500 border-2 border-background grid place-items-center"><Check size={11} className="text-white" /></span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">{ch.tag}</span>
                        <h3 className="font-display font-extrabold text-xl leading-none">{ch.name}</h3>
                      </div>
                      <span className="text-xs font-bold text-muted-foreground shrink-0">{done}/{ch.lessons.length}</span>
                    </div>
                    <div className="relative pl-6">
                      <div className="absolute left-[9px] top-3 bottom-3 w-0.5 bg-white/10" />
                      <div className="space-y-3">
                        {ch.lessons.map((l) => {
                          const isCurrent = l.id === currentId;
                          return (
                            <div key={l.id} className="relative">
                              <div className={`absolute -left-[22px] top-1/2 -translate-y-1/2 h-5 w-5 rounded-full grid place-items-center border-2 border-background z-10 ${l.completed ? "bg-emerald-500" : l.locked ? "bg-yellow-500" : isCurrent ? col.solid : "bg-white/20"}`}>
                                {l.completed ? <Check size={11} className="text-white" /> : l.locked ? <Lock size={10} className="text-white" /> : null}
                                {isCurrent && <span className={`absolute inset-0 rounded-full ${col.solid} animate-ping opacity-60`} />}
                              </div>
                              <motion.button data-testid={`lesson-row-${l.id}`} whileHover={{ x: 4 }} onClick={() => l.locked ? navigate("/app/premium") : navigate(`/lesson/${l.id}`)}
                                className={`w-full flex items-center gap-3 glass rounded-2xl p-4 text-left ${isCurrent ? `border ${col.border}` : l.locked ? "border border-yellow-500/20" : ""}`}>
                                <div className={`h-10 w-10 rounded-xl grid place-items-center shrink-0 ${l.completed ? "bg-emerald-500/15" : l.locked ? "bg-yellow-500/10" : col.bg}`}>
                                  {l.completed ? <CheckCircle2 size={18} className="text-emerald-400" /> : l.locked ? <Lock size={16} className="text-yellow-400" /> : <Icon name={l.type === "quiz" ? "quiz" : l.type === "flashcards" ? "layers" : l.type === "checklist" ? "checklist" : l.type === "masterclass" ? "graduation" : "book"} size={17} className={col.text} />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold text-sm truncate">{l.title}</h4>
                                    {isCurrent && <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full ${col.bg} ${col.text} shrink-0`}>Start</span>}
                                  </div>
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    {l.locked ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-300 border border-yellow-500/25"><Crown size={10} /> Premium</span>
                                      : <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-muted-foreground">{TYPE_LABEL[l.type] || l.type}</span>}
                                    <span className="text-[11px] text-muted-foreground">+{l.xp} XP</span>
                                  </div>
                                </div>
                                {l.completed ? <span className="text-[11px] font-bold text-emerald-400">Done</span> : l.locked ? <Lock size={15} className="text-yellow-400 shrink-0" /> : <Play size={16} className="text-muted-foreground shrink-0" />}
                              </motion.button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
